import { useMemo } from "react"

import { useAuthStore } from "@/stores/auth"
import { Scopes } from "@/types/auth"

const RESOURCE_CONFIG = {
  users: {
    label: "Users",
    singular: "User",
    actions: {
      read: Scopes.USERS_READ,
      create: Scopes.USERS_CREATE,
      update: Scopes.USERS_UPDATE,
      delete: Scopes.USERS_DELETE,
    },
    managerScopes: [
      Scopes.USERS_UPDATE,
      Scopes.USERS_DELETE,
      Scopes.SYSTEM_ADMIN,
    ],
  },
  files: {
    label: "Files",
    singular: "File",
    actions: {
      read: Scopes.FILES_READ,
      create: Scopes.FILES_CREATE,
      update: Scopes.FILES_UPDATE,
      delete: Scopes.FILES_DELETE,
    },
    managerScopes: [
      Scopes.FILES_UPDATE,
      Scopes.FILES_DELETE,
      Scopes.SYSTEM_ADMIN,
    ],
  },
  households: {
    label: "Households",
    singular: "Household",
    actions: {
      read: Scopes.HOUSEHOLDS_READ,
      create: Scopes.HOUSEHOLDS_CREATE,
      update: Scopes.HOUSEHOLDS_UPDATE,
      delete: Scopes.HOUSEHOLDS_DELETE,
    },
    managerScopes: [
      Scopes.HOUSEHOLDS_UPDATE,
      Scopes.HOUSEHOLDS_DELETE,
      Scopes.SYSTEM_ADMIN,
    ],
  },
  sessions: {
    label: "Sessions",
    singular: "Session",
    actions: {
      read: Scopes.SESSIONS_READ,
      create: Scopes.SESSIONS_CREATE,
      update: Scopes.SESSIONS_UPDATE,
      delete: Scopes.SESSIONS_DELETE,
    },
    managerScopes: [
      Scopes.SESSIONS_UPDATE,
      Scopes.SESSIONS_DELETE,
      Scopes.SYSTEM_ADMIN,
    ],
  },
  classTemplates: {
    label: "ClassTemplates",
    singular: "ClassTemplate",
    actions: {
      read: Scopes.CLASSTEMPLATES_READ,
      create: Scopes.CLASSTEMPLATES_CREATE,
      update: Scopes.CLASSTEMPLATES_UPDATE,
      delete: Scopes.CLASSTEMPLATES_DELETE,
    },
    managerScopes: [
      Scopes.CLASSTEMPLATES_UPDATE,
      Scopes.CLASSTEMPLATES_DELETE,
      Scopes.SYSTEM_ADMIN,
    ],
  },
  students: {
    label: "Students",
    singular: "Student",
    actions: {
      read: Scopes.STUDENTS_READ,
      create: Scopes.STUDENTS_CREATE,
      update: Scopes.STUDENTS_UPDATE,
      delete: Scopes.STUDENTS_DELETE,
    },
    managerScopes: [
      Scopes.STUDENTS_UPDATE,
      Scopes.STUDENTS_DELETE,
      Scopes.SYSTEM_ADMIN,
    ],
  },
  enrollments: {
    label: "Enrollments",
    singular: "Enrollment",
    actions: {
      read: Scopes.ENROLLMENTS_READ,
      create: Scopes.ENROLLMENTS_CREATE,
      update: Scopes.ENROLLMENTS_UPDATE,
      delete: Scopes.ENROLLMENTS_DELETE,
    },
    managerScopes: [
      Scopes.ENROLLMENTS_UPDATE,
      Scopes.ENROLLMENTS_DELETE,
      Scopes.SYSTEM_ADMIN,
    ],
  },
  classMeetings: {
    label: "ClassSessions",
    singular: "ClassSession",
    actions: {
      read: Scopes.CLASSMEETINGS_READ,
      create: Scopes.CLASSMEETINGS_CREATE,
      update: Scopes.CLASSMEETINGS_UPDATE,
      delete: Scopes.CLASSMEETINGS_DELETE,
    },
    managerScopes: [
      Scopes.CLASSMEETINGS_UPDATE,
      Scopes.CLASSMEETINGS_DELETE,
      Scopes.SYSTEM_ADMIN,
    ],
  },
  attendances: {
    label: "Attendances",
    singular: "Attendance",
    actions: {
      read: Scopes.ATTENDANCES_READ,
      create: Scopes.ATTENDANCES_CREATE,
      update: Scopes.ATTENDANCES_UPDATE,
      delete: Scopes.ATTENDANCES_DELETE,
    },
    managerScopes: [
      Scopes.ATTENDANCES_UPDATE,
      Scopes.ATTENDANCES_DELETE,
      Scopes.SYSTEM_ADMIN,
    ],
  },
} as const

type ResourceKey = keyof typeof RESOURCE_CONFIG

type ResourceActionFlags<C extends Record<string, { label: string }>> = {
  [K in keyof C as `canRead${C[K]["label"]}`]: boolean
} & { [K in keyof C as `canCreate${C[K]["label"]}`]: boolean } & {
  [K in keyof C as `canUpdate${C[K]["label"]}`]: boolean
} & { [K in keyof C as `canDelete${C[K]["label"]}`]: boolean }

type ResourceManagerFlags<C extends Record<string, { singular: string }>> = {
  [K in keyof C as `is${C[K]["singular"]}Manager`]: boolean
}

type ResourceFlags = ResourceActionFlags<typeof RESOURCE_CONFIG> &
  ResourceManagerFlags<typeof RESOURCE_CONFIG>

type SystemFlags = {
  canReadSystem: boolean
  canUpdateSystem: boolean
  canAdminSystem: boolean
  canManageSystem: boolean
  canAccessAdmin: boolean
}

type BasePermissionState = {
  scopes: string[]
  hasScope: (scope: string) => boolean
  hasAnyScope: (permissions: string[]) => boolean
  hasAllScopes: (permissions: string[]) => boolean
  isAdmin: boolean
  hasPermission: (scope: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasAllPermissions: (permissions: string[]) => boolean
  permissions: string[]
  can: (scope: string) => boolean
  canAny: (permissions: string[]) => boolean
  canAll: (permissions: string[]) => boolean
}

type PermissionState = BasePermissionState & ResourceFlags & SystemFlags

const SYSTEM_CONFIG = {
  label: "System",
  actions: {
    read: Scopes.SYSTEM_READ,
    update: Scopes.SYSTEM_UPDATE,
  },
  adminScope: Scopes.SYSTEM_ADMIN,
} as const

export const usePermissions = (): PermissionState => {
  const { scopes, hasScope, hasAnyScope, hasAllScopes, isAdmin } =
    useAuthStore()

  return useMemo(() => {
    const admin = isAdmin()

    const resourceFlags: Record<string, boolean> = {}
    ;(
      Object.values(RESOURCE_CONFIG) as Array<
        (typeof RESOURCE_CONFIG)[ResourceKey]
      >
    ).forEach((config: (typeof RESOURCE_CONFIG)[ResourceKey]) => {
      resourceFlags[`canRead${config.label}`] = hasScope(config.actions.read)
      resourceFlags[`canCreate${config.label}`] = hasScope(
        config.actions.create,
      )
      resourceFlags[`canUpdate${config.label}`] = hasScope(
        config.actions.update,
      )
      resourceFlags[`canDelete${config.label}`] = hasScope(
        config.actions.delete,
      )

      const managerScopes = config.managerScopes ?? []
      resourceFlags[`is${config.singular}Manager`] =
        managerScopes.length > 0
          ? hasAnyScope([...managerScopes] as string[])
          : false
    })

    const canManageSystem = hasScope(SYSTEM_CONFIG.adminScope)
    const systemFlags: SystemFlags = {
      canReadSystem: hasScope(SYSTEM_CONFIG.actions.read),
      canUpdateSystem: hasScope(SYSTEM_CONFIG.actions.update),
      canAdminSystem: canManageSystem,
      canManageSystem,
      canAccessAdmin: admin || canManageSystem,
    }

    // [RZ-GENERATOR:PERMISSIONS]
    const baseState: BasePermissionState = {
      scopes,
      hasScope,
      hasAnyScope,
      hasAllScopes,
      isAdmin: admin,
      hasPermission: hasScope,
      hasAnyPermission: hasAnyScope,
      hasAllPermissions: hasAllScopes,
      permissions: scopes,
      can: (scope: string) => hasScope(scope),
      canAny: (permissions: string[]) => hasAnyScope(permissions),
      canAll: (permissions: string[]) => hasAllScopes(permissions),
    }

    return {
      ...baseState,
      ...(resourceFlags as ResourceFlags),
      ...systemFlags,
    }
  }, [scopes, hasScope, hasAnyScope, hasAllScopes, isAdmin])
}

const DEFAULT_RESOURCE_PERMISSIONS = {
  canRead: false,
  canCreate: false,
  canUpdate: false,
  canDelete: false,
  canManage: false,
}

export const useResourcePermissions = (resource: ResourceKey | "system") => {
  const { hasScope, hasAnyScope } = usePermissions()

  return useMemo(() => {
    if (resource === "system") {
      const canAdmin = hasScope(SYSTEM_CONFIG.adminScope)
      return {
        canRead: hasScope(SYSTEM_CONFIG.actions.read),
        canUpdate: hasScope(SYSTEM_CONFIG.actions.update),
        canAdmin,
        canManage: canAdmin,
      }
    }

    // [RZ-GENERATOR:RESOURCE-CASES]
    const config = RESOURCE_CONFIG[resource]
    if (!config) {
      return { ...DEFAULT_RESOURCE_PERMISSIONS }
    }

    const permissions = { ...DEFAULT_RESOURCE_PERMISSIONS }

    permissions.canRead = hasScope(config.actions.read)
    permissions.canCreate = hasScope(config.actions.create)
    permissions.canUpdate = hasScope(config.actions.update)
    permissions.canDelete = hasScope(config.actions.delete)

    const managerScopes = config.managerScopes ?? []
    permissions.canManage = managerScopes.length
      ? hasAnyScope([...managerScopes] as string[])
      : false

    return permissions
  }, [resource, hasScope, hasAnyScope])
}

export {
  useAuth,
  useHasScope,
  useHasAnyScope,
  useHasAllScopes,
  useIsAdmin,
} from "./permissions/useAuth"

export { useUserWithPermissions } from "./permissions/useUserWithPermissions"

export {
  useRBACPermissions,
  type Permission,
  type PermissionGroup,
} from "./permissions/useRBACPermissions"

export { usePermissionGate } from "./permissions/usePermissionGate"
export { useRoles } from "./permissions/useRoles"

export default usePermissions
